import { useState } from 'react'
import { motion } from 'framer-motion'
import { Globe, Mail } from 'lucide-react'
import SectionTitle from './SectionTitle'
import emailjs from '@emailjs/browser'

const Contact = () => {
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitStatus, setSubmitStatus] = useState(null)

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    })
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      await emailjs.send(
        import.meta.env.VITE_EMAILJS_SERVICE_ID,
        import.meta.env.VITE_EMAILJS_TEMPLATE_ID,
        {
          name: formData.name,
          email: formData.email,
          message: formData.message,
          reply_to: formData.email,
          from_name: formData.name,
          to_email: 'wisdomlotachukwu@gmail.com',
        },
        import.meta.env.VITE_EMAILJS_PUBLIC_KEY
      )
      setSubmitStatus('success')
      setFormData({ name: '', email: '', message: '' })
    } catch (error) {
      console.error('Failed to send:', error)
      setSubmitStatus('error')
    }

    setIsSubmitting(false)
    setTimeout(() => setSubmitStatus(null), 3000)
  }

  return (
    <section id="contact" className="bg-ds-off-white py-16 sm:py-20 lg:py-28">
      <div className="max-w-full mx-auto px-4 sm:px-6 lg:px-12">
        <SectionTitle title="CONTACT" />

        <div className="grid lg:grid-cols-2 gap-10 sm:gap-12 lg:gap-20">
          {/* Left side - Company Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6 }}
            className="space-y-6 sm:space-y-8"
          >
            <p className="font-body text-base lg:text-lg text-ds-navy/80 leading-relaxed">
              We'd love to hear from you. For inquiries about our products or company, please 
              reach out using the details below.
            </p>

            {/* Company Name */}
            <div>
              <h3 className="font-heading text-xl sm:text-2xl lg:text-3xl font-extrabold text-ds-navy tracking-tight">
                DAILY STRIDE LLC .
              </h3>
            </div>

            {/* Contact Details */}
            <div className="space-y-3 sm:space-y-4">
              <motion.a
                href="https://www.dailystridellc.com"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 sm:gap-4 text-ds-navy hover:text-ds-blue transition-colors"
                whileHover={{ x: 5 }}
              >
                <Globe size={24} className="contact-icon flex-shrink-0" />
                <span className="font-body text-base lg:text-lg">www.dailystridellc.com</span>
              </motion.a>
              
              <motion.a
                href="mailto:admin@dailystridellc.com"
                className="flex items-center gap-3 sm:gap-4 text-ds-navy hover:text-ds-blue transition-colors"
                whileHover={{ x: 5 }}
              >
                <Mail size={24} className="contact-icon flex-shrink-0" />
                <span className="font-body text-base lg:text-lg">admin@dailystridellc.com</span>
              </motion.a>
            </div>
          </motion.div>

          {/* Right side - Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
              {/* Name Field */}
              <div>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Name"
                  required
                  className="contact-input bg-ds-navy/5"
                />
              </div>
              {/* Email Field */}
              <div>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  required
                  className="contact-input bg-ds-navy/5"
                />
              </div>

              {/* Message Field */}
              <div>
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Message"
                  rows={5}
                  required
                  className="contact-input resize-none bg-ds-navy/5"
                />
              </div>

              {/* Submit Button */}
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.98 }}
                className={`send-btn ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
              >
                {isSubmitting ? 'SENDING...' : 'SEND MESSAGE'}
              </motion.button>
                
              {/* Status Messages */}
              {submitStatus === 'success' && (
                <p className="text-green-600 text-center font-medium">Message sent successfully!</p>
              )}
              {submitStatus === 'error' && (
                <p className="text-red-600 text-center font-medium">Failed to send. Please try again.</p>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

export default Contact
